/* eslint-disable @typescript-eslint/indent */
import {
  useRef,
  useState,
  FC,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Alert,
  Block,
  Input,
  Text,
} from 'src/__generated__/graphqlTypes';
import { createTicketProxy } from 'src/api/externalApi';
import { Store } from 'app-redux/types/storeTypes';
import {
  IdNameType,
  ValidationAndValue,
  ZendeskTicketRequestData,
} from 'types/objectTypes';
import { FeedbackForm } from 'components/Sections/ContactUsGetInTouch/FeedbackForm';
import { setItemInObjectBySlug } from 'lib/sharedMethods.service';
import { setLoadingState } from 'app-redux/actions/appActions';
import { getCountriesListProxy } from 'src/api/countriesApi';
import { Logger } from 'lib/logger';
import {
  FeedbackFormInterfaceStateValues,
  FeedbackFormContainerSlug as Slug,
  FeedbackFormContainerInterface,
} from 'components/Sections/ContactUsGetInTouch/FeedbackForm/declarations';

const feedbackFormSlugs = {
  'name-input': Slug.NAME_INPUT,
  'email-input': Slug.EMAIL_INPUT,
  'phone-number-input': Slug.PHONE_NUMBER_INPUT,
  'country-input': Slug.COUNTRY_INPUT,
  'request-type-input': Slug.REQUEST_TYPE_INPUT,
  'message-input': Slug.MESSAGE_INPUT,
  modal: Slug.MODAL,
};

const applyDefaultValue = <T, >(
  value: T,
  input: Input,
  isValid: boolean | null = null,
): ValidationAndValue<T> => ({
    value,
    isValid,
    alerts: input.alertsCollection?.items as Array<Alert> || [],
    placeholder: input.placeholder || '',
    sysId: input.sys.id,
  });

const FeedbackFormContainer: FC<FeedbackFormContainerInterface> = ({ feedbackForm }) => {
  const { user } = useSelector((store: Store) => store.server.auth);
  const userAgent = useSelector((store: Store) => store.client.app.userAgent);
  const { viewerDevice } = useSelector((store: Store) => store.server.app);
  const dispatch = useDispatch();
  const [isModalVisible, setModalVisibilityState] = useState<boolean>(false);
  const closeModal = () => setModalVisibilityState(false);
  const fetchedCountries = useRef<Array<Text>>([]);
  const [countries, setCountries] = useState<Array<Text>>(fetchedCountries.current);

  const fetchCountries = async () => {
    try {
      dispatch(setLoadingState(true));

      if (fetchedCountries.current.length > 0) {
        return;
      }

      const countries = await getCountriesListProxy();
      fetchedCountries.current = countries;
      setCountries(countries);
    } catch (e: any) {
      Logger.error(e);
    } finally {
      dispatch(setLoadingState(false));
    }
  };

  const searchInCountries = (text: string) => {
    setCountries(fetchedCountries.current
      .filter((country) => country.text?.toLowerCase()
        .includes(text?.toLowerCase()!)));
  };

  const inputs = (feedbackForm.contentTypesCollection?.items as Array<Input>) || [];
  const { link, title } = feedbackForm;
  const reducer = setItemInObjectBySlug(feedbackFormSlugs);

  const {
    nameInput,
    emailInput,
    phoneNumberInput,
    countryInput,
    requestTypeInput,
    messageInput,
    modal,
  } = inputs.reduce(reducer, {} as Record<Slug, Input>);

  const initialInputValues = {
    name: applyDefaultValue<string>('', nameInput),
    email: applyDefaultValue<string>('', emailInput),
    phoneNumber: applyDefaultValue<string>('', phoneNumberInput),
    country: applyDefaultValue<string>('', countryInput),
    requestType: applyDefaultValue<IdNameType<string>>({ id: '', name: '' }, requestTypeInput),
    message: applyDefaultValue<string>('', messageInput),
  };
  const [
    inputValues,
    setInputValues,
  ] = useState<FeedbackFormInterfaceStateValues>(initialInputValues);

  const handleSubmitForm = async (values: FeedbackFormInterfaceStateValues) => {
    const zendeskData: ZendeskTicketRequestData = {
      country: values.country.value,
      customerId: user?.customerId,
      emailAddressOnAccount: user?.email,
      emailAddressProvided: values.email.value,
      message: values.message.value,
      name: values.name.value,
      phoneNumber: values.phoneNumber.value,
      requestType: values.requestType.value,
      platform: viewerDevice,
      browser: userAgent?.browser?.name,
      browserVersion: userAgent?.browser?.version,
    };

    try {
      dispatch(setLoadingState(true));
      await createTicketProxy(zendeskData);
      setModalVisibilityState(true);
      setInputValues(initialInputValues);
    } catch (e: any) {
      Logger.error(e.data);
    } finally {
      dispatch(setLoadingState(false));
    }
  };

  return (
    <FeedbackForm
      inputValues={inputValues}
      setInputValues={setInputValues}
      handleSubmitForm={handleSubmitForm}
      searchInCountries={searchInCountries}
      feedbackForm={feedbackForm}
      modal={modal as Block}
      isModalVisible={isModalVisible}
      closeModal={closeModal}
      button={link!}
      title={title!}
      countries={countries}
      requestTypeOptions={requestTypeInput.optionsCollection?.items as Array<Text> || []}
      fetchCountries={fetchCountries}
    />
  );
};

export default FeedbackFormContainer;
